import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ease } from '../../core/format/motion';
import { inputCls } from './styles';
type TabItem<K extends string> = {
  key: K;
  label: string;
  count?: number;
};
type Props<K extends string> = {
  tabs: readonly TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  label: string;
  /**
   * Koppelt de tabs aan een <TabPanel id={…}>: elke tab krijgt `aria-controls`, het paneel
   * `aria-labelledby` naar de actieve tab. Zonder id is het een filter zonder eigen paneel.
   */
  id?: string;
  /** 'line' voor hoofdtabs boven een lijst, 'pill' voor een compacte schakelaar. */
  variant?: 'line' | 'pill';
  className?: string;
};
const slide = {
  type: 'tween',
  duration: 0.2,
  ease
} as const;
/** Vanaf zoveel tabs wordt het op een telefoon een keuzelijst. */
const SELECT_FROM = 5;

/** Eén plek voor de id's, zodat tab en paneel altijd naar elkaar wijzen. */
const tabId = (id: string, key: string) => `${id}-tab-${key}`;
export const panelId = (id: string) => `${id}-panel`;

/**
 * Eén tabbalk voor het hele beheer, volgens het WAI-ARIA-patroon "tabs met automatische activatie":
 * - één tabstop: Tab gaat de balk in op de actieve tab en er weer uit naar het paneel;
 * - ← → wisselen (rondgaand), Home en End springen naar de eerste en laatste;
 * - de actieve tab schuift zacht mee; met "minder beweging" zonder animatie;
 * - vanaf 5 tabs op een telefoon de eigen keuzelijst van het toestel.
 */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  label,
  id,
  variant = 'line',
  className = ''
}: Props<K>) {
  const indicator = useId();
  const reduce = useReducedMotion();
  const listRef = useRef<HTMLDivElement>(null);
  const onKey = (e: KeyboardEvent, i: number) => {
    const last = tabs.length - 1;
    const next = e.key === 'ArrowRight' ? i === last ? 0 : i + 1 : e.key === 'ArrowLeft' ? i === 0 ? last : i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? last : -1;
    if (next < 0 || next === i) return;
    e.preventDefault();
    onChange(tabs[next].key);
    // De focus volgt de tab die ná de wissel echt geselecteerd is. Weigert de pagina de wissel
    // (bijv. "nog niet bewaard" in het dossier), dan blijft hij op de huidige tab staan,
    // in plaats van op een tab die er niet bij hoort.
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]');
      el?.focus();
      // Bij een scrollrij op mobiel de tab in beeld houden.
      el?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    });
  };
  const line = variant === 'line';
  const asSelect = tabs.length >= SELECT_FROM;
  return <>
      {asSelect &&
    // Veel tabs op een telefoon: de eigen keuzelijst van het toestel in plaats van een
    // scrollrij waarvan je de helft niet ziet. Aantallen staan erbij, zodat "3 nieuw" niet verdwijnt.
    <label className={`block sm:hidden ${className}`} data-id="element-5554" data-mp="28pcjbl62x2r0">
          <span className="sr-only" data-id="element-5555" data-mp="3qjtqejj9t4yd">{label}</span>
          <select value={value} onChange={e => onChange(e.target.value as K)} aria-controls={id ? panelId(id) : undefined} className={inputCls} data-id="element-5556" data-mp="3qn7ubw7l1m7x">
            {tabs.map(t => <option key={t.key} value={t.key} data-id="element-5557" data-mp="3hm17z6i95h6r">
                {t.label}
                {typeof t.count === 'number' && t.count > 0 ? ` (${t.count})` : ''}
              </option>)}
          </select>
        </label>}
      <div className={`-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] ${asSelect ? 'hidden sm:block' : ''} ${className}`} data-id="element-5558" data-mp="i4tk5fn3x147">
        <div ref={listRef} role="tablist" aria-label={label} aria-orientation="horizontal" className={line ? 'flex min-w-max gap-6 border-b border-line' : 'inline-flex min-w-max gap-1 rounded-full border border-line bg-white p-1'} data-id="element-5559" data-mp="4cvifpvq67i2">
          {tabs.map((t, i) => {
          const on = t.key === value;
          const hasCount = typeof t.count === 'number' && t.count > 0;
          return <button key={t.key} id={id ? tabId(id, t.key) : undefined} type="button" role="tab" aria-selected={on} aria-controls={id ? panelId(id) : undefined} tabIndex={on ? 0 : -1} onClick={() => onChange(t.key)} onKeyDown={e => onKey(e, i)} className={`relative inline-flex items-center gap-2 whitespace-nowrap transition-colors duration-150 ease-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/50 ${line ? `min-h-11 rounded-sm pb-3 pt-2 text-[14px] sm:min-h-0 sm:pt-1 ${on ? 'text-ink' : 'text-muted hover:text-ink'}` : `rounded-full px-4 py-2 text-[13px] focus-visible:ring-offset-1 ${on ? 'text-shell' : 'text-muted hover:text-ink'}`}`} data-id="element-5560" data-mp="34dsld7hdzgqx" data-mpi={i}>
                {on && <motion.span layoutId={`${indicator}-indicator`} transition={reduce ? {
              duration: 0
            } : slide} className={line ? 'absolute inset-x-0 -bottom-px h-0.5 bg-ink' : 'absolute inset-0 rounded-full bg-ink'} aria-hidden="true" data-id="element-5561" data-mp="i7z77x8w393m" />}
                <span className="relative" data-id="element-5562" data-mp="115ifuzrfqppi">{t.label}</span>
                {hasCount && <span className={`relative min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] leading-5 tabular-nums ${on && !line ? 'bg-shell/20 text-shell' : 'bg-clay text-shell'}`} data-id="element-5563" data-mp="163k0vq8f8z3m">
                    {t.count}
                    {/* "Bestellingen 3" zegt een schermlezer anders zonder context. */}
                    <span className="sr-only" data-id="element-5564" data-mp="1ou1qvn1vdgla"> {t.count === 1 ? 'item' : 'items'}</span>
                  </span>}
              </button>;
        })}
        </div>
      </div>
    </>;
}
type PanelProps<K extends string> = {
  /** Dezelfde id als op <Tabs id>. */
  id: string;
  /** De actieve tab; het paneel heet naar die tab. */
  value: K;
  children: ReactNode;
  className?: string;
};

/**
 * Het paneel onder <Tabs>. Eén paneel dat van inhoud wisselt (de inhoud per tab wordt pas
 * getekend als hij actief is). Focusbaar, zodat Tab vanaf de tabbalk direct in de inhoud landt
 * ook als die met tekst begint in plaats van een knop.
 */
export function TabPanel<K extends string>({
  id,
  value,
  children,
  className
}: PanelProps<K>) {
  return <div role="tabpanel" id={panelId(id)} aria-labelledby={tabId(id, value)} tabIndex={0} className={`rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/40 focus-visible:ring-offset-4 focus-visible:ring-offset-sand ${className ?? ''}`} data-id="element-5565" data-mp="hemx2k4pxra0">
      {children}
    </div>;
}